import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import './htmlInput.css';

import {
    ContentState,
    convertFromHTML,
    convertToRaw,
    EditorState,
} from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { useEffect, useRef, useState } from 'react';
import { Editor } from 'react-draft-wysiwyg';

import { useFormReset } from '@/dialogs/item-editor/Context/FormResetContext';
import { useTabNavigation } from '@/hooks/useTabNavigation';
import { cn } from '@/services/utils';

import { Textarea } from '../Textarea';

interface HTMLInputInterface {
    id?: string;
    placeholder?: string;
    readOnly?: boolean;
    onChange?: any;
    onBlur?: any;
    onFocus?: any;
    name?: string;
    rows?: number;
    wrapperClassName?: string;
    value?: string;
    htmlFocus?:boolean
    [key: string]: any;
}

const HTMLInput = ({
    placeholder,
    id,
    readOnly,
    onChange,
    rows = 5,
    wrapperClassName = '',
    value,
    htmlFocus,
    ...props
}: HTMLInputInterface) => {

    const formResetContext = useFormReset();

    let isResetting = false;
    try {
        isResetting = formResetContext?.isResetting || false;
    } catch (error) {
        isResetting = false;
    }

    const [editorState, setEditorState] = useState(() => {
        if (value) {
            const blocksFromHTML = convertFromHTML(value);
            return EditorState.createWithContent(
                ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap)
            );
        } else {
            return EditorState.createEmpty();
        }
    });
    const editorRef = useRef<Editor | null>(null);

    useEffect(() => {
        if (isResetting) return;
        if (htmlFocus && editorRef.current) {
            editorRef.current.focusEditor();
        }
    }, [htmlFocus, isResetting]);

    useEffect(()=>{
        if (!value) {
            return setEditorState(EditorState.createEmpty());
        }
    },[value]);

    const [htmlCode, setHtmlCode] = useState('');
    const [showCode, setShowCode] = useState(false);

    const lineHeight = 15;
    const minHeight = rows * lineHeight;

    const handleEditorStateChange = (newEditorState: EditorState) => {
        const contentState = newEditorState.getCurrentContent();
        const rawHtml = draftToHtml(convertToRaw(contentState));

        if (rawHtml !== htmlCode) {
            setHtmlCode(rawHtml);
            onChange && onChange(rawHtml);
        }

        setEditorState(newEditorState);
    };

    const handleTabNavigation = useTabNavigation();

    const handleHtmlCodeChange = (event: any) => {
        setHtmlCode(event.target.value);
        const blocksFromHtml = convertFromHTML(event.target.value);
        const state = ContentState.createFromBlockArray(
            blocksFromHtml.contentBlocks,
            blocksFromHtml.entityMap,
        );

        setEditorState(EditorState.createWithContent(state));
        onChange && onChange(event.target.value);
    };

    const toggleCodeVisibility = () => {
        setShowCode(!showCode);
    };

    const ShowEditorCode = () => (
        <div className="rdw-option-wrapper" onClick={toggleCodeVisibility}>
            {showCode ? 'Hide Code' : 'Show Code'}
        </div>
    );
    const toolbarCustomButtons = [<ShowEditorCode key={id} />];

    return (
        <div
            className={cn(
                'html-input-wrapper tw-w-full bordered-input-wrapper lg-font-dynamic tw-text-t-primary',
                readOnly && 'tw-bg-fill-secondary tw-text-t-inactive tw-cursor-not-allowed',
                wrapperClassName
            )}
        >
            <Editor
                key={id}
                editorState={editorState}
                onEditorStateChange={handleEditorStateChange}
                toolbarCustomButtons={toolbarCustomButtons}
                toolbarHidden={readOnly}
                placeholder={placeholder}
                tabIndex={1}
                onTab={handleTabNavigation}
                readOnly={readOnly}
                ref={editorRef}
                editorStyle={{ minHeight: `${minHeight}px`, backgroundColor: 'white' }}
                editorClassName={cn(readOnly && 'tw-bg-fill-secondary tw-text-t-inactive')}
                {...props}
            />
            {showCode && (
                <div className="code-area">
                    <Textarea
                        id={id}
                        className={cn(readOnly && 'tw-bg-fill-secondary tw-text-t-inactive tw-cursor-not-allowed')}
                        value={htmlCode}
                        tabIndex={1}
                        onChange={handleHtmlCodeChange}
                        readOnly={readOnly}
                        rows={rows}
                        {...props}
                    />
                </div>
            )}
        </div>
    );
};

export { HTMLInput };
